import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import Header from '../landing/Header';
import Footer from '../landing/Footer';
import { Button } from '../ui/button';
import { ArrowLeft, Check } from 'lucide-react';

// WhatsApp icon component
const WhatsAppIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const AboutPage = () => {
  const { isEnglish, langPrefix } = useLanguage();
  
  const WHATSAPP_NUMBER = '+201221948907';
  const whatsappLink = `https://wa.me/${WHATSAPP_NUMBER.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(
    isEnglish ? 'Hello, I have a question about your excursions.' : 'Bonjour, j\'ai une question sur vos excursions.'
  )}`;

  const content = {
    fr: {
      title: 'Qui suis-je ?',
      intro: 'Je m\'appelle Maxime et je vis à Hurghada, en Égypte.',
      introSub: 'J\'ai créé Hurghada Excursion pour proposer des excursions fiables, bien organisées et sans mauvaises surprises.',
      approachTitle: 'Mon approche',
      approachText: 'Contrairement aux plateformes impersonnelles, je sélectionne personnellement chaque excursion que je propose.',
      approachText2: 'Toutes les excursions proposées ont été testées personnellement avant d\'être mises en ligne.',
      approachText3: 'Je travaille uniquement avec des prestataires locaux de confiance afin de garantir :',
      approachList: [
        'une bonne organisation',
        'des groupes raisonnables',
        'une expérience conforme à ce qui est annoncé'
      ],
      whyTitle: 'Pourquoi ce site ?',
      whyText: 'À Hurghada, l\'offre est immense, mais la qualité est très variable.',
      whyGoal: 'Mon objectif :',
      whyList: [
        'éviter les arnaques',
        'faire gagner du temps',
        'permettre de réserver sereinement'
      ],
      whyConclusion: 'C\'est pourquoi je privilégie le contact direct via WhatsApp.',
      paymentTitle: 'Paiement & confiance',
      paymentText: 'Le paiement en ligne sert uniquement d\'acompte.',
      paymentText2: 'Le solde est réglé sur place.',
      paymentText3: 'Aucune pression : vous pouvez toujours me contacter avant de réserver.',
      ctaText: 'Une question ? Écrivez-moi sur WhatsApp',
      backLink: 'Retour à l\'accueil'
    },
    en: {
      title: 'Who am I?',
      intro: 'My name is Maxime and I live in Hurghada, Egypt.',
      introSub: 'I created Hurghada Excursion to offer reliable, well-organized trips without unpleasant surprises.',
      approachTitle: 'My approach',
      approachText: 'I personally select every excursion listed.',
      approachText2: 'All excursions offered have been personally tested before being listed.',
      approachText3: 'I work only with trusted local operators to ensure:',
      approachList: [
        'proper organization',
        'reasonable group sizes',
        'experiences that match what is advertised'
      ],
      whyTitle: 'Why this website?',
      whyText: 'Hurghada offers many excursions, but quality varies.',
      whyGoal: 'My goal:',
      whyList: [
        'help you avoid scams',
        'save you time',
        'let you book with confidence'
      ],
      whyConclusion: 'That\'s why I encourage direct contact via WhatsApp.',
      paymentTitle: 'Payment & trust',
      paymentText: 'Online payments are deposits only.',
      paymentText2: 'The balance is paid on site.',
      paymentText3: 'No pressure, you can always contact me before booking.',
      ctaText: 'Any questions? Contact me on WhatsApp',
      backLink: 'Back to home'
    }
  };

  const t = isEnglish ? content.en : content.fr;

  return (
    <div className="min-h-screen bg-gradient-to-b from-teal-50 to-white flex flex-col">
      <Header />
      
      <main className="flex-1 pt-24 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto">
          {/* Back link */}
          <Link 
            to={langPrefix || '/'} 
            className="inline-flex items-center text-teal-600 hover:text-teal-700 mb-8 text-sm"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            {t.backLink}
          </Link>

          {/* Founder Photo */}
          <div className="flex justify-center mb-8">
            <img
              src="https://res.cloudinary.com/dzx5jx8fr/image/upload/v1768578730/maximehurghadaexcursionquisuisje_z8zrc6.jpg"
              alt="Maxime - Fondateur Hurghada Excursions"
              className="w-32 h-32 sm:w-40 sm:h-40 rounded-full object-cover shadow-lg border-4 border-white"
            />
          </div>

          {/* Title */}
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-800 text-center mb-6">
            {t.title}
          </h1>

          {/* Intro */}
          <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
            <p className="text-lg text-slate-700 mb-3">{t.intro}</p>
            <p className="text-slate-600">{t.introSub}</p>
          </div>

          {/* My Approach */}
          <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
            <h2 className="text-xl font-semibold text-slate-800 mb-4">{t.approachTitle}</h2>
            <p className="text-slate-600 mb-3">{t.approachText}</p>
            <p className="text-slate-600 mb-3">{t.approachText2}</p>
            <p className="text-slate-600 mb-3">{t.approachText3}</p>
            <ul className="space-y-2">
              {t.approachList.map((item, index) => (
                <li key={index} className="flex items-start gap-2 text-slate-600">
                  <Check className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Why this website */}
          <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
            <h2 className="text-xl font-semibold text-slate-800 mb-4">{t.whyTitle}</h2>
            <p className="text-slate-600 mb-3">{t.whyText}</p>
            <p className="text-slate-700 font-medium mb-2">{t.whyGoal}</p>
            <ul className="space-y-2 mb-4">
              {t.whyList.map((item, index) => (
                <li key={index} className="flex items-start gap-2 text-slate-600">
                  <Check className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <p className="text-slate-600">{t.whyConclusion}</p>
          </div>

          {/* Payment & Trust */}
          <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
            <h2 className="text-xl font-semibold text-slate-800 mb-4">{t.paymentTitle}</h2>
            <p className="text-slate-600 mb-2">{t.paymentText}</p>
            <p className="text-slate-600 mb-2">{t.paymentText2}</p>
            <p className="text-slate-600">{t.paymentText3}</p>
          </div>

          {/* CTA */}
          <div className="text-center">
            <Button
              asChild
              size="lg"
              className="bg-emerald-500 hover:bg-emerald-600 text-white px-8 py-6 rounded-full text-base font-medium transition-all duration-300 hover:shadow-lg"
            >
              <a href={whatsappLink} target="_blank" rel="noopener noreferrer">
                <span className="mr-2 hidden sm:inline">📲</span>
                <span className="hidden sm:inline">{t.ctaText}</span>
                <span className="sm:hidden">{isEnglish ? 'Any questions? WhatsApp' : 'Une question ? WhatsApp'}</span>
                <WhatsAppIcon className="w-5 h-5 ml-2" />
              </a>
            </Button>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default AboutPage;
